import { BasePaymentGatewayAdapter } from './BasePaymentGatewayAdapter';
import {
  PaymentData,
  PaymentToken,
  GatewayConfig,
  TokenizationError,
  GatewayInitializationError
} from '../../types/payment';

// Declare Stripe global type
declare global {
  interface Window {
    Stripe?: any;
  }
}

/**
 * Stripe payment gateway adapter using Stripe Elements
 * Handles secure tokenization via Stripe's JavaScript SDK
 */
export class StripeGatewayAdapter extends BasePaymentGatewayAdapter {
  private stripe: any = null;
  private elements: any = null;
  private cardElement: any = null;

  constructor() {
    super('stripe');
  }

  /**
   * Initialize Stripe by loading the SDK and setting up Elements
   */
  async initialize(config: GatewayConfig): Promise<void> {
    try {
      this.config = config;

      // Load Stripe.js SDK
      await this.loadScript('https://js.stripe.com/v3/', 'stripe-js');

      if (!window.Stripe) {
        throw new GatewayInitializationError(
          'Stripe SDK failed to load',
          'sdk_load_failed',
          this.provider
        );
      }

      // Initialize Stripe instance
      this.stripe = window.Stripe(config.publicKey);

      if (!this.stripe) {
        throw new GatewayInitializationError(
          'Failed to initialize Stripe with public key',
          'stripe_init_failed',
          this.provider
        );
      }

      this.initialized = true;
      console.log('Stripe gateway initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Stripe gateway:', error);
      throw error instanceof GatewayInitializationError
        ? error
        : new GatewayInitializationError(
            `Stripe initialization failed: ${error}`,
            'init_error',
            this.provider
          );
    }
  }

  /**
   * Create Stripe Elements for secure card input
   * This should be called when rendering the payment form
   */
  createCardElement(containerId: string): void {
    this.requireInitialized();

    if (!this.elements) {
      this.elements = this.stripe.elements();
    }

    if (!this.cardElement) {
      this.cardElement = this.elements.create('card', {
        style: {
          base: {
            fontSize: '16px',
            color: '#424770',
            '::placeholder': {
              color: '#aab7c4',
            },
          },
          invalid: {
            color: '#9e2146',
          },
        },
      });
    }

    const container = document.getElementById(containerId);
    if (container) {
      this.cardElement.mount(`#${containerId}`);
    }
  }

  /**
   * Tokenize payment data using Stripe Elements
   * This creates a payment_method in Stripe's system
   */
  async tokenizePayment(paymentData: PaymentData): Promise<PaymentToken> {
    this.requireInitialized();

    try {
      console.log('[StripeGateway] Starting tokenization...');

      // Skip card field validation for Stripe Elements (Stripe validates internally)
      // Only validate cardholder name and billing address
      if (!paymentData.cardholderName.trim()) {
        throw new TokenizationError(
          'Validation failed: Cardholder name is required',
          'validation_error',
          this.provider
        );
      }

      if (!this.cardElement) {
        throw new TokenizationError(
          'Card element not created. Call createCardElement() first.',
          'no_card_element',
          this.provider
        );
      }

      console.log('[StripeGateway] Creating payment method with Stripe...');

      // Create payment method using Stripe Elements
      const { error, paymentMethod } = await this.stripe.createPaymentMethod({
        type: 'card',
        card: this.cardElement,
        billing_details: {
          name: paymentData.cardholderName,
          address: {
            line1: paymentData.billingAddress.street,
            city: paymentData.billingAddress.city,
            state: paymentData.billingAddress.state,
            postal_code: paymentData.billingAddress.zipCode,
            country: paymentData.billingAddress.country,
          },
        },
      });

      console.log('[StripeGateway] Stripe createPaymentMethod response:', { error, paymentMethod });

      if (error) {
        throw new TokenizationError(
          error.message || 'Failed to create payment method',
          error.code || 'tokenization_failed',
          this.provider
        );
      }

      if (!paymentMethod) {
        throw new TokenizationError(
          'No payment method returned from Stripe',
          'no_payment_method',
          this.provider
        );
      }

      // Extract card details for token
      const card = paymentMethod.card;

      return {
        token: paymentMethod.id,
        type: 'payment_method_id',
        last_four_digits: card.last4,
        card_brand: card.brand,
        exp_month: card.exp_month,
        exp_year: card.exp_year,
        gateway_provider: this.provider
      };

    } catch (error) {
      console.error('Stripe tokenization failed:', error);

      if (error instanceof TokenizationError) {
        throw error;
      }

      throw new TokenizationError(
        `Tokenization failed: ${error}`,
        'tokenization_error',
        this.provider
      );
    }
  }

  /**
   * Clean up Stripe resources
   */
  cleanup(): void {
    if (this.cardElement) {
      this.cardElement.destroy();
      this.cardElement = null;
    }

    this.elements = null;
    this.stripe = null;

    super.cleanup();
  }

  /**
   * Check if Stripe is available in the browser
   */
  isAvailable(): boolean {
    return typeof window !== 'undefined';
  }
}
